/**
 * TeamSettingsTab - Team info, feature toggles, JIRA templates, and command permissions
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChatOutlined as ChatIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';
import {
  teamApi,
  agentApi,
  getDmContacts,
  createDmContact,
  deleteDmContact,
  sendTestDm,
  PromptConfig,
  JiraTicketTemplates,
  JiraTemplateConfig,
  DmContact,
  DmContactCreate,
} from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import CommandPermissionsTable from '@/components/team/CommandPermissionsTable';
import TeamGoogleChatSetup from '@/components/team/TeamGoogleChatSetup';
import type { Team } from '@/types';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const JIRA_ISSUE_TYPES = ['Task', 'Story', 'Bug', 'Epic', 'Initiative'] as const;
type JiraIssueType = (typeof JIRA_ISSUE_TYPES)[number];

const DEFAULT_JIRA_TEMPLATES: JiraTicketTemplates = {
  Task: {
    template: `## What needs to be done\n{what}\n\n## Steps\n{steps}\n\n## Resources\n{resources}\n\n---\n_Created via AI Wingman_`,
    placeholders: {
      what: '[Describe the task clearly and concisely]',
      steps: '1. [First step]\n2. [Second step]\n3. [Third step]',
      resources: '- [Documentation link]\n- [Related ticket]\n- [Code repository]',
    },
  },
  Story: {
    template: `## User Need\n{user_need}\n\n## Implementation Steps\n{steps}\n\n## Done When\n{done_when}\n\n## Resources\n{resources}\n\n---\n_Created via AI Wingman_`,
    placeholders: {
      user_need: '[Who needs this and why? e.g., As a user, I need X so that Y]',
      steps: '1. [Implementation step 1]\n2. [Implementation step 2]\n3. [Implementation step 3]',
      done_when: '- [ ] [Completion criteria 1]\n- [ ] [Completion criteria 2]',
      resources: '- [Design mockups]\n- [API documentation]\n- [Related stories]',
    },
  },
  Bug: {
    template: `## Current Issue\n{issue}\n\n## How to Reproduce\n{reproduce}\n\n## Expected Result\n{expected}\n\n## Resources\n{resources}\n\n---\n_Created via AI Wingman_`,
    placeholders: {
      issue: "[What's broken? Be specific about the problem]",
      reproduce: '1. [Step 1]\n2. [Step 2]\n3. [Observe the issue]',
      expected: '[What should happen instead]',
      resources: '- [Screenshots/logs]\n- [Environment: Browser/OS]\n- [Related issues]',
    },
  },
  Epic: {
    template: `## Goal\n{goal}\n\n## Why It Matters\n{why}\n\n## Key Deliverables\n{deliverables}\n\n## Success Metrics\n{metrics}\n\n## Resources\n{resources}\n\n---\n_Created via AI Wingman_`,
    placeholders: {
      goal: '[What are we building and why?]',
      why: '[Business value and impact]',
      deliverables: '- [Major deliverable 1]\n- [Major deliverable 2]\n- [Major deliverable 3]',
      metrics: '- [How we measure success 1]\n- [How we measure success 2]',
      resources: '- [Product requirements]\n- [Technical design]\n- [Dependencies]',
    },
  },
  Initiative: {
    template: `## Strategic Goal\n{goal}\n\n## Problem We're Solving\n{problem}\n\n## Approach\n{approach}\n\n## Key Results\n{key_results}\n\n## Resources\n{resources}\n\n---\n_Created via AI Wingman_`,
    placeholders: {
      goal: '[Strategic objective this initiative supports]',
      problem: '[What problem are we solving?]',
      approach: '[High-level solution approach]',
      key_results: '- [Measurable result 1]\n- [Measurable result 2]\n- [Measurable result 3]',
      resources: '- [Business case]\n- [Stakeholders]\n- [Timeline/roadmap]',
    },
  },
};

export default function TeamSettingsTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const { isTeamLead } = useAuth();
  const canEdit = isTeamLead(teamId!);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Team info state
  const [teamInfo, setTeamInfo] = useState<Partial<Team>>({});
  const [teamInfoChanged, setTeamInfoChanged] = useState(false);

  // Prompt config state
  const [formData, setFormData] = useState<Partial<PromptConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // JIRA templates state
  const [selectedIssueType, setSelectedIssueType] = useState<JiraIssueType>('Task');
  const [jiraTemplates, setJiraTemplates] = useState<JiraTicketTemplates>(DEFAULT_JIRA_TEMPLATES);

  // DM contacts dialog state
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [dmForm, setDmForm] = useState<DmContactCreate>({ email: '', display_name: '', note: '' });
  const [dmFormError, setDmFormError] = useState('');
  const [deleteConfirmContact, setDeleteConfirmContact] = useState<DmContact | null>(null);


  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: promptConfig } = useQuery({
    queryKey: ['prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: agentConfig, isLoading: agentConfigLoading } = useQuery({
    queryKey: ['agent-config', teamId],
    queryFn: () => agentApi.getConfig(teamId!).then((res) => res.data),
    enabled: !!teamId && !!formData.enable_agent,
  });

  const { data: dmContacts = [], isLoading: dmContactsLoading } = useQuery({
    queryKey: ['dmContacts', teamId],
    queryFn: () => getDmContacts(teamId!),
    enabled: !!teamId,
  });

  const { data: oauthConfig, isLoading: oauthConfigLoading } = useQuery({
    queryKey: ['team-google-oauth', teamId],
    queryFn: () => teamApi.getGoogleOAuthConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const gmailReAuthMutation = useMutation({
    mutationFn: () => teamApi.initGoogleOAuth(teamId!).then((res) => res.data),
    onSuccess: (data) => {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        data.authorization_url,
        'google-oauth-reauth-popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );
      if (popup) {
        const poll = setInterval(() => {
          if (popup.closed) {
            clearInterval(poll);
            queryClient.invalidateQueries({ queryKey: ['team-google-oauth', teamId] });
          }
        }, 500);
      }
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to start re-authorization flow.', severity: 'error' });
    },
  });

  const allowExternalMutation = useMutation({
    mutationFn: (allow: boolean) =>
      teamApi.updatePromptConfig(teamId!, { allow_external_email_recipients: allow } as Partial<PromptConfig>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-google-oauth', teamId] });
      setSnackbar({ open: true, message: 'Setting saved.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to save: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const createDmMutation = useMutation({
    mutationFn: (data: DmContactCreate) => createDmContact(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dmContacts', teamId] });
      setDmDialogOpen(false);
      setDmForm({ email: '', display_name: '', note: '' });
      setDmFormError('');
      setSnackbar({ open: true, message: 'Contact added successfully.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to add contact: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const deleteDmMutation = useMutation({
    mutationFn: (contactId: string) => deleteDmContact(teamId!, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dmContacts', teamId] });
      setDeleteConfirmContact(null);
      setSnackbar({ open: true, message: 'Contact removed.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to remove contact: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const testDmMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => sendTestDm(email, 'Test DM from AI Wingman'),
    onSuccess: (_data, variables) => {
      setSnackbar({ open: true, message: `Test DM sent to ${variables.email}.`, severity: 'success' });
    },
    onError: (error: Error, variables) => {
      setSnackbar({ open: true, message: `Failed to send DM to ${variables.email}: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  useEffect(() => {
    if (team) {
      setTeamInfo({
        name: team.name,
        description: team.description,
        jira_project_key: team.jira_project_key,
        jira_base_url: team.jira_base_url,
        timezone: team.timezone,
        vision_enabled: team.vision_enabled,
      });
      setTeamInfoChanged(false);
    }
  }, [team]);

  useEffect(() => {
    if (promptConfig) {
      setFormData(promptConfig);
      if (promptConfig.jira_ticket_templates) {
        setJiraTemplates({ ...DEFAULT_JIRA_TEMPLATES, ...promptConfig.jira_ticket_templates });
      } else {
        setJiraTemplates(DEFAULT_JIRA_TEMPLATES);
      }
      setHasChanges(false);
    }
  }, [promptConfig]);

  const teamUpdateMutation = useMutation({
    mutationFn: (data: Partial<Team>) => teamApi.update(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      setSnackbar({ open: true, message: 'Team info saved successfully!', severity: 'success' });
      setTeamInfoChanged(false);
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to save: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PromptConfig>) => teamApi.updatePromptConfig(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-config', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-prompt-config', teamId] });
      setSnackbar({ open: true, message: 'Settings saved successfully!', severity: 'success' });
      setHasChanges(false);
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to save: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const handleTeamInfoChange = (field: string, value: string) => {
    setTeamInfo((prev) => ({ ...prev, [field]: value }));
    setTeamInfoChanged(true);
  };

  const handleChange = (field: keyof PromptConfig, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (tabValue === 0 && teamInfoChanged) {
      teamUpdateMutation.mutate({
        name: teamInfo.name,
        description: teamInfo.description,
        jira_project_key: teamInfo.jira_project_key,
        jira_base_url: teamInfo.jira_base_url,
        timezone: teamInfo.timezone,
      });
    } else if ((tabValue === 1 || tabValue === 2) && (hasChanges || teamInfoChanged)) {  // Feature Settings or JIRA Templates
      if (hasChanges) {
        const saveData = {
          ...formData,
          jira_ticket_templates: formData.jira_ticket_templates === null ? null : jiraTemplates,
        };
        updateMutation.mutate(saveData);
      }
      if (teamInfoChanged) {
        teamUpdateMutation.mutate({ vision_enabled: teamInfo.vision_enabled });
      }
    }
  };

  const handleJiraTemplateChange = (field: keyof JiraTemplateConfig, value: string | Record<string, string>) => {
    setJiraTemplates((prev) => {
      const updated = { ...prev, [selectedIssueType]: { ...prev[selectedIssueType], [field]: value } };
      setFormData((prevForm) => ({ ...prevForm, jira_ticket_templates: updated }));
      return updated;
    });
    setHasChanges(true);
  };

  const handlePlaceholderChange = (key: string, value: string) => {
    handleJiraTemplateChange('placeholders', { ...jiraTemplates[selectedIssueType].placeholders, [key]: value });
  };

  const handleResetIssueType = () => {
    setJiraTemplates((prev) => {
      const updated = { ...prev, [selectedIssueType]: DEFAULT_JIRA_TEMPLATES[selectedIssueType] };
      setFormData((prevForm) => ({ ...prevForm, jira_ticket_templates: updated }));
      return updated;
    });
    setHasChanges(true);
  };

  const handleResetAllTemplates = () => {
    setJiraTemplates(DEFAULT_JIRA_TEMPLATES);
    setFormData((prev) => ({ ...prev, jira_ticket_templates: null }));
    setHasChanges(true);
  };

  if (!teamId) return null;

  const isSaving = teamUpdateMutation.isPending || updateMutation.isPending;
  // Tabs 1 (Feature Settings) and 2 (JIRA Templates) track local form changes.
  // Tabs 3 (Integrations), 4 (DM Contacts), and 5 (Permissions) manage their own state internally.
  const activeTabHasChanges = tabValue === 0 ? teamInfoChanged : tabValue === 1 || tabValue === 2 ? hasChanges : false;

  const handleDmFormSubmit = () => {
    if (!dmForm.email.trim()) {
      setDmFormError('Email is required.');
      return;
    }
    setDmFormError('');
    createDmMutation.mutate({
      email: dmForm.email.trim(),
      display_name: dmForm.display_name?.trim() || undefined,
      note: dmForm.note?.trim() || undefined,
    });
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Team Info" />
          <Tab label="Feature Settings" />
          <Tab label="JIRA Templates" />
          <Tab label="Integrations" />
          <Tab label="DM Contacts" />
          <Tab label="Permissions" />
        </Tabs>
      </Box>

      {/* Team Info */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Basic team details. Changes are saved directly.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Team Name"
                  fullWidth
                  value={teamInfo.name || ''}
                  onChange={(e) => handleTeamInfoChange('name', e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={teamInfo.description || ''}
                  onChange={(e) => handleTeamInfoChange('description', e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="JIRA Project Key"
                  fullWidth
                  value={teamInfo.jira_project_key || ''}
                  onChange={(e) => handleTeamInfoChange('jira_project_key', e.target.value)}
                  disabled={!canEdit}
                  placeholder="e.g. PROJ"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="JIRA Base URL"
                  fullWidth
                  value={teamInfo.jira_base_url || ''}
                  onChange={(e) => handleTeamInfoChange('jira_base_url', e.target.value)}
                  disabled={!canEdit}
                  placeholder="e.g. https://jira.example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Timezone"
                  fullWidth
                  value={teamInfo.timezone || ''}
                  onChange={(e) => handleTeamInfoChange('timezone', e.target.value)}
                  disabled={!canEdit}
                  placeholder="e.g. Europe/Berlin"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Feature Settings */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Feature Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enable or disable specific bot behaviors and integrations.
            </Typography>

            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_auto_replies ?? true} onChange={(e) => handleChange('enable_auto_replies', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Chatbot Auto-Replies</Typography>
                      <Typography variant="body2" color="text.secondary">Bot answers every new question in the team&apos;s Google Chat space. When disabled, it replies only when @mentioned or to <code>!</code> commands. Workflows, newsletter and Jira automation keep running.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_github_issues ?? false} onChange={(e) => handleChange('enable_github_issues', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">GitHub Issues Tracking</Typography>
                      <Typography variant="body2" color="text.secondary">Enable GitHub Issues tracking dashboard and automatic JIRA ticket creation.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_agent ?? false} onChange={(e) => handleChange('enable_agent', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Claude Code Agent</Typography>
                      <Typography variant="body2" color="text.secondary">Enable the AI coding agent. Users can use <code>!agent</code> commands in Google Chat to create tasks, and the Agent tab becomes visible.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit || !formData.enable_agent}
                  control={<Switch checked={formData.enable_jira_spy ?? false} onChange={(e) => handleChange('enable_jira_spy', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">JIRA AI Refinement</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Agent automatically picks up Jira tickets with the configured label (requires Agent feature).
                      </Typography>
                    </Box>
                  }
                />
                {formData.enable_jira_spy && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    The agent will automatically pick up tickets in your Jira project{' '}
                    <strong>({team?.jira_project_key || '[not configured]'})</strong> with the label configured in the Agent tab.
                    <br />
                    Configure the label to monitor in <strong>Agent → Configuration</strong>.
                  </Alert>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit || !formData.enable_agent || !agentConfig?.spec_driven_enabled}
                  control={
                    <Switch
                      checked={formData.enable_jira_ai_execution ?? false}
                      onChange={(e) => handleChange('enable_jira_ai_execution', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1">JIRA AI Execution</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Agent automatically picks up Jira tickets with the configured label and
                        implements them hands-off — no approval step. Requires Spec-Driven mode
                        (Agent → Configuration) to be enabled, or tickets won't be picked up.
                      </Typography>
                    </Box>
                  }
                />
                {!agentConfigLoading && formData.enable_jira_ai_execution && !agentConfig?.spec_driven_enabled && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Enable Spec-Driven mode in <strong>Agent → Configuration</strong> first —
                    without it, tickets won't be picked up at all.
                  </Alert>
                )}
                {formData.enable_jira_ai_execution && agentConfig?.spec_driven_enabled && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    The agent will automatically pick up tickets in your Jira project{' '}
                    <strong>({team?.jira_project_key || '[not configured]'})</strong> with the
                    label configured in the Agent tab, and implement them fully automatically.
                    <br />
                    Configure the label to monitor in <strong>Agent → Configuration</strong>.
                  </Alert>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_jira_team_view ?? false} onChange={(e) => handleChange('enable_jira_team_view', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Jira Team View</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show a Team View tab with epic priorities, active kanban by assignee, and backlog. Requires Jira to be configured for this team.
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={teamInfo.vision_enabled ?? false} onChange={(e) => { setTeamInfo((prev) => ({ ...prev, vision_enabled: e.target.checked })); setTeamInfoChanged(true); }} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Vision / Image Analysis</Typography>
                      <Typography variant="body2" color="text.secondary">Allow the bot to analyze images and screenshots shared in Google Chat. Images are sent to your configured AI provider for analysis.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_jira_commands ?? false} onChange={(e) => handleChange('enable_jira_commands', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Enable JIRA Commands</Typography>
                      <Typography variant="body2" color="text.secondary">Users can use <code>!jira</code> commands in Google Chat to create tickets, search issues, and get project statistics.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_expert_tagging ?? true} onChange={(e) => handleChange('enable_expert_tagging', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Expert Tagging</Typography>
                      <Typography variant="body2" color="text.secondary">Include @mentions for experts based on their expertise keywords.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_prioritization_escalation ?? true} onChange={(e) => handleChange('enable_prioritization_escalation', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Prioritization Escalation</Typography>
                      <Typography variant="body2" color="text.secondary">Suggest contacting the team lead when priority keywords are mentioned.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_multiple_thread_responses ?? false} onChange={(e) => handleChange('enable_multiple_thread_responses', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Multiple Thread Responses</Typography>
                      <Typography variant="body2" color="text.secondary">Bot responds to every message in a thread. When disabled, use <code>!ask</code> for follow-ups.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_response_caching ?? false} onChange={(e) => handleChange('enable_response_caching', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Response Caching</Typography>
                      <Typography variant="body2" color="text.secondary">Reuse cached answers for similar questions.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.disable_auto_expert_append ?? false} onChange={(e) => handleChange('disable_auto_expert_append', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Disable Auto Expert Section</Typography>
                      <Typography variant="body2" color="text.secondary">Don&apos;t auto-append the &quot;ASSIGNED TEAM MEMBER&quot; section.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_web_fetch ?? true} onChange={(e) => handleChange('enable_web_fetch', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Enable Web URL Fetching</Typography>
                      <Typography variant="body2" color="text.secondary">Bot can fetch and read content from URLs included in user messages.</Typography>
                    </Box>
                  }
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControlLabel
                  disabled={!canEdit}
                  control={<Switch checked={formData.enable_proactive_docs_fetch ?? true} onChange={(e) => handleChange('enable_proactive_docs_fetch', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1">Enable Proactive Documentation Fetching</Typography>
                      <Typography variant="body2" color="text.secondary">Bot automatically fetches live content from configured documentation links and repositories.</Typography>
                    </Box>
                  }
                />
              </Paper>
            </Stack>
          </CardContent>
        </Card>
      </TabPanel>

      {/* JIRA Templates */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>JIRA Ticket Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure description templates for each JIRA issue type. Use placeholders like {'{what}'}, {'{steps}'} that AI will fill in.
                </Typography>
              </Box>
              {canEdit && (
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<ResetIcon />} onClick={handleResetIssueType}>Reset Type</Button>
                  <Button size="small" startIcon={<ResetIcon />} onClick={handleResetAllTemplates} color="warning">Reset All</Button>
                </Stack>
              )}
            </Box>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Issue Type</InputLabel>
              <Select value={selectedIssueType} label="Issue Type" onChange={(e) => setSelectedIssueType(e.target.value as JiraIssueType)}>
                {JIRA_ISSUE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" gutterBottom>Template</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Markdown template for the ticket description. Placeholders in {'{braces}'} will be filled by AI.
            </Typography>
            <TextField
              multiline rows={12} fullWidth
              value={jiraTemplates[selectedIssueType]?.template || ''}
              onChange={(e) => handleJiraTemplateChange('template', e.target.value)}
              disabled={!canEdit}
              sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace' }, mb: 3 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>Placeholder Defaults</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Default text shown when AI doesn&apos;t provide a value for a placeholder.
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(jiraTemplates[selectedIssueType]?.placeholders || {}).map(([key, value]) => (
                <Grid item xs={12} key={key}>
                  <TextField
                    label={`{${key}}`} fullWidth multiline rows={2} value={value}
                    onChange={(e) => handlePlaceholderChange(key, e.target.value)}
                    disabled={!canEdit}
                    sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace' } }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Integrations */}
      <TabPanel value={tabValue} index={3}>
        <Stack spacing={2}>
          <TeamGoogleChatSetup teamId={teamId} />

          {/* Gmail scope card — only shown when Google is connected */}
          {!oauthConfigLoading && oauthConfig?.has_token && (
            <Card>
              <CardHeader
                avatar={<EmailOutlinedIcon color="primary" />}
                title="Gmail Workflow Outputs"
                subheader="Allow workflows to send emails via this team's authorized Google account."
              />
              <CardContent>
                <Stack spacing={2}>
                  {!oauthConfig.google_oauth_has_gmail_scope && (
                    <Alert
                      severity="info"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => gmailReAuthMutation.mutate()}
                          disabled={gmailReAuthMutation.isPending}
                        >
                          {gmailReAuthMutation.isPending ? 'Starting...' : 'Re-authorize Google'}
                        </Button>
                      }
                    >
                      Re-authorize Google to enable Gmail-based workflow outputs. The current
                      authorization does not include the Gmail send scope.
                    </Alert>
                  )}
                  {oauthConfig.google_oauth_has_gmail_scope && (
                    <Alert severity="success">
                      Gmail send scope is authorized. Workflows can send emails via this team's
                      Google account.
                    </Alert>
                  )}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControlLabel
                      disabled={!canEdit}
                      control={
                        <Switch
                          checked={oauthConfig.allow_external_email_recipients}
                          onChange={(e) => allowExternalMutation.mutate(e.target.checked)}
                          disabled={allowExternalMutation.isPending}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1">Allow External Email Recipients</Typography>
                          <Typography variant="body2" color="text.secondary">
                            If off, workflow emails can only be sent to addresses in your team's
                            domain. Turn on to allow sending to external addresses.
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </TabPanel>

      {/* DM Contacts */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Designated DM Contacts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Google Chat accounts the bot can send direct messages to (e.g. for notifications and workflow output).
                </Typography>
              </Box>
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDmForm({ email: '', display_name: '', note: '' });
                    setDmFormError('');
                    setDmDialogOpen(true);
                  }}
                  size="small"
                >
                  Add Contact
                </Button>
              )}
            </Box>

            {dmContactsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : dmContacts.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No DM contacts configured. Add a contact to enable direct message delivery.
                </Typography>
              </Paper>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dmContacts.map((contact) => (
                    <TableRow key={contact.id} hover>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.display_name ?? <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        {contact.note ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {contact.note}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Send test DM">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => testDmMutation.mutate({ email: contact.email })}
                                disabled={testDmMutation.isPending}
                              >
                                <ChatIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Remove contact">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteConfirmContact(contact)}
                                  disabled={deleteDmMutation.isPending}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Contact Dialog */}
        <Dialog open={dmDialogOpen} onClose={() => setDmDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add DM Contact</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <TextField
                label="Email"
                required
                fullWidth
                value={dmForm.email}
                onChange={(e) => setDmForm((f) => ({ ...f, email: e.target.value }))}
                error={!!dmFormError}
                helperText={dmFormError || 'Google account email used in Google Chat'}
                autoFocus
              />
              <TextField
                label="Display Name (optional)"
                fullWidth
                value={dmForm.display_name}
                onChange={(e) => setDmForm((f) => ({ ...f, display_name: e.target.value }))}
              />
              <TextField
                label="Note (optional)"
                fullWidth
                multiline
                rows={2}
                value={dmForm.note}
                onChange={(e) => setDmForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Team lead for Platform — receives P1 alerts"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDmDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleDmFormSubmit}
              disabled={createDmMutation.isPending}
            >
              {createDmMutation.isPending ? 'Adding...' : 'Add Contact'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirmContact} onClose={() => setDeleteConfirmContact(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove Contact</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Remove <strong>{deleteConfirmContact?.email}</strong> from DM contacts? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmContact(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteConfirmContact && deleteDmMutation.mutate(deleteConfirmContact.id)}
              disabled={deleteDmMutation.isPending}
            >
              {deleteDmMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Command Permissions */}
      <TabPanel value={tabValue} index={5}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Command Permissions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure which roles can execute specific bot commands.
            </Typography>
            <CommandPermissionsTable teamId={teamId} />
          </CardContent>
        </Card>
      </TabPanel>

      {activeTabHasChanges && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You have unsaved changes. Don&apos;t forget to save before leaving.
        </Alert>
      )}

      {canEdit && activeTabHasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!activeTabHasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
